import React, { useState } from 'react';
import type { LegalDoc } from '../../api/legalApi';

// Переиспользуемый просмотрщик юр-документов: вертикальные кнопки выбора + markdown.
// Используется и на странице «Правовая информация», и в шаге согласия онбординга.
function renderInline(text: string, keyPrefix: string): React.ReactNode[] {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) => {
    if (p.startsWith('**') && p.endsWith('**')) {
      return <strong key={`${keyPrefix}-b${i}`}>{p.slice(2, -2)}</strong>;
    }
    return <React.Fragment key={`${keyPrefix}-t${i}`}>{p}</React.Fragment>;
  });
}

export function LegalMarkdown({ md }: { md: string }) {
  const lines = md.replace(/\r\n/g, '\n').split('\n');
  const out: React.ReactNode[] = [];
  let list: string[] = [];
  const flushList = (idx: number) => {
    if (list.length) {
      out.push(
        <ul key={`ul-${idx}`} style={{ margin: '8px 0', paddingLeft: 20 }}>
          {list.map((li, i) => (
            <li key={i} style={{ marginBottom: 6, lineHeight: 1.55 }}>{renderInline(li, `li-${idx}-${i}`)}</li>
          ))}
        </ul>
      );
      list = [];
    }
  };
  lines.forEach((raw, idx) => {
    const line = raw.trimEnd();
    if (/^###\s+/.test(line)) { flushList(idx); out.push(<h4 key={idx} style={{ margin: '16px 0 6px', fontSize: 15 }}>{renderInline(line.replace(/^###\s+/, ''), `h4-${idx}`)}</h4>); return; }
    if (/^##\s+/.test(line)) { flushList(idx); out.push(<h3 key={idx} style={{ margin: '20px 0 8px', fontSize: 17 }}>{renderInline(line.replace(/^##\s+/, ''), `h3-${idx}`)}</h3>); return; }
    if (/^#\s+/.test(line)) { flushList(idx); out.push(<h2 key={idx} style={{ margin: '4px 0 14px', fontSize: 20 }}>{renderInline(line.replace(/^#\s+/, ''), `h2-${idx}`)}</h2>); return; }
    if (/^>\s?/.test(line)) {
      flushList(idx);
      out.push(
        <div key={idx} style={{ borderLeft: '3px solid var(--rf-accent, #2e86ff)', background: 'var(--rf-surface, rgba(255,255,255,0.06))', padding: '10px 14px', borderRadius: 8, margin: '12px 0', fontSize: 13.5, lineHeight: 1.5 }}>
          {renderInline(line.replace(/^>\s?/, ''), `q-${idx}`)}
        </div>
      );
      return;
    }
    if (/^[-*]\s+/.test(line)) { list.push(line.replace(/^[-*]\s+/, '')); return; }
    if (line.trim() === '') { flushList(idx); return; }
    flushList(idx);
    out.push(<p key={idx} style={{ margin: '8px 0', lineHeight: 1.6, fontSize: 14.5 }}>{renderInline(line, `p-${idx}`)}</p>);
  });
  flushList(lines.length);
  return <div style={{ color: 'var(--rf-text, #e8ecf4)', wordBreak: 'break-word' }}>{out}</div>;
}

export function LegalDocsView({ docs, initial, bounded }: { docs: LegalDoc[]; initial?: LegalDoc['key']; bounded?: boolean }) {
  const [active, setActive] = useState<LegalDoc['key']>(initial ?? docs[0]?.key ?? 'tos');
  const doc = docs.find((d) => d.key === active) ?? docs[0];
  if (!docs.length) return null;

  return (
    <div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 14 }} role="tablist">
        {docs.map((d) => {
          const isActive = d.key === active;
          return (
            <button
              key={d.key}
              type="button"
              role="tab"
              aria-selected={isActive}
              onClick={() => setActive(d.key)}
              style={{
                width: '100%', textAlign: 'left', padding: '13px 16px', borderRadius: 14,
                fontSize: 15, fontWeight: 600, cursor: 'pointer', lineHeight: 1.3,
                transition: 'background .2s, border-color .2s, color .2s',
                border: isActive ? '1px solid transparent' : '1px solid var(--rf-border, rgba(255,255,255,0.12))',
                background: isActive ? 'var(--rf-accent-grad, linear-gradient(135deg,#2e86ff,#7c5cff))' : 'var(--rf-surface, rgba(255,255,255,0.06))',
                color: isActive ? '#fff' : 'var(--rf-text, #e8ecf4)',
                boxShadow: isActive ? 'var(--rf-glow, 0 6px 20px rgba(46,134,255,0.35))' : 'none',
              }}
            >
              {d.title}
            </button>
          );
        })}
      </div>
      {doc && (
        <div
          className="rf-card"
          style={bounded ? { padding: 16, maxHeight: '42vh', overflowY: 'auto', WebkitOverflowScrolling: 'touch' } : { padding: 16 }}
        >
          <LegalMarkdown md={doc.markdown} />
          <div style={{ marginTop: 14, paddingTop: 10, borderTop: '1px solid var(--rf-border, rgba(255,255,255,0.1))', fontSize: 12, color: 'var(--rf-text-2, #96a0b5)' }}>
            Версия документа: {doc.version}
          </div>
        </div>
      )}
    </div>
  );
}

export default LegalDocsView;
